import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faQuestionCircle, faShip, faStar } from '@fortawesome/free-solid-svg-icons';
import bannerImage from '../../images/TermsPage/MrSea_TermsPage_HeroImage.png';
import tripAdvisorIcon from '../../images/HomePage/MrSea2025_TripAdvisorIcon.svg';
import SEOHead from '../components/Common/SEOHead';
import WebPreloader from '../components/Common/WebPreloader';
import CustomBoatLeadModal from '../components/HomePage/CustomBoatLeadModal';
import '../styles/home/FAQ.css';

const FAQsPage = () => {
  const [openTab, setOpenTab] = useState(null);
  const [showPreloader, setShowPreloader] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const toggleTab = (index) => {
    setOpenTab(openTab === index ? null : index);
  };

  const faqData = [
    { question: "I've never been on a boat. What should I expect?", answer: "Expect amazing views, good music, and ocean vibes! Our crew is used to working with first-time guests — we'll walk you through everything, from safety instructions to the best spots to chill and take photos." },
    { question: "Will I get seasick?", answer: "Most of our routes are calm, especially in Biscayne Bay. If you're prone to motion sickness, we suggest taking precautions ahead of time, like eating a light meal before boarding and staying hydrated to help ensure a smooth and enjoyable experience." },
    { question: "Can I bring kids or babies on board?", answer: "Yes! All of our boats are family-friendly. Let us know in advance so we can prepare child-size life jackets." },
    { question: "Can we bring food and drinks on board?", answer: "Absolutely. You're welcome to bring your own, or ask us about our catering and beverage packages for a stress-free day." },
    { question: "Do you provide cups or plates?", answer: "We recommend bringing your own cups, utensils, napkins, and any other essentials you may need. That said, we do keep a few extras on board just in case you run out — we've got you covered!" },
    { question: "How early should we arrive before departure?", answer: "Please arrive at least 15–20 minutes early so we can load up and start on time. Miami traffic is no joke!" },
    { question: "What's included in the price?", answer: "Our base price includes the boat, crew, fuel, and cooler with ice. Add-ons like decorations, food, or special water toys are extra." },
    { question: "Are there any hidden fees?", answer: "Never. We're upfront with everything. You'll receive a detailed quote before confirming." },
    { question: "Is gratuity included?", answer: "Gratuity is not included but always appreciated. 15–20% is standard if you're happy with the service!" },
    { question: "Can I decorate the boat for a birthday or bachelorette?", answer: "Yes! We offer decoration packages starting at $210, or you're welcome to bring your own. Just let us know in advance." },
    { question: "Can we play our own music?", answer: "Definitely. All our boats have Bluetooth sound systems — just connect and vibe." },
    { question: "Can I surprise someone with this trip?", answer: "Of course! Let us know if it's a proposal, birthday, or special surprise and we'll help make it unforgettable." },
    { question: "Can we swim or use floaties?", answer: "Absolutely! Swimming is one of the highlights of the experience. Most of our boats come with a floating mat included in the rate. You're also more than welcome to bring your own floaties or water toys if you'd like!" },
    { question: "What happens if it rains?", answer: "Light rain doesn't usually affect trips, but for heavy storms, we'll work with you to reschedule. Your safety comes first." },
    { question: "Is there a bathroom on board?", answer: "Most of our larger boats have a bathroom — check the listing or ask before booking if it's essential." },
    { question: "Is there A/C on board?", answer: "Some of our boats do have air conditioning, but not all... especially our older or budget-friendly options. If A/C is a must for you, just ask! We're happy to guide you to the best fit." },
    { question: "How do I book?", answer: "Since every trip is unique, we don't process bookings directly on our website. Instead, just reach out and one of our team members will walk you through everything — from picking the right boat to finalizing your custom invoice." },
    { question: "Do you offer payment plans?", answer: "We require a deposit to reserve your spot. Full payment is due before your trip. Contact us if you need flexible options." },
    { question: "What's your cancellation policy?", answer: "We do our best to be flexible. If bad weather affects your trip, we'll work with you to find the best possible solution for both sides. For all other cancellations, please make sure to review our full policy <a href=\"https://mrseamiami.com/terms\" target=\"_blank\" rel=\"noopener noreferrer\">here</a> before booking." },
    { question: "Can we drive the boat ourselves?", answer: "For safety and legal reasons, all our charters come with a licensed captain — you're in great hands!" },
    { question: "Is alcohol allowed?", answer: "Yes! Alcohol is allowed on board for guests 21 and older. Please drink responsibly, your safety is our priority. The captain reserves the right to end the trip if anyone behaves in a disruptive or dangerous manner." },
  ];

  const leftColumnData = faqData.filter((_, index) => index % 2 === 0);
  const rightColumnData = faqData.filter((_, index) => index % 2 !== 0);

  return (
    <div className="faqs-page" style={{ background: '#006098', minHeight: '100vh' }}>
      <SEOHead pageType="faqs" />
      {/* Hero Section */}
      <section className="faqs-hero">
        <div className="faqs-hero__bg-image" style={{ backgroundImage: `url(${bannerImage})` }}></div>
        <div className="faqs-hero__overlay"></div>
        <div className="faqs-hero__content">
            <h1 className="faqs-hero__title">Frequently Asked Questions</h1>
            <p className="faqs-hero__subtitle">Find quick answers to the most common questions we receive. We're here to help you!</p>
        </div>
        <div className="faqs-hero__wave-container">
            <svg className="waves" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
            <defs>
            <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
            </defs>
            <g className="parallax">
            <use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(0, 96, 152, 0.2)" />
            <use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(0, 96, 152, 0.4)" />
            <use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(0, 96, 152, 0.6)" />
            <use xlinkHref="#gentle-wave" x="48" y="7" fill="#006098" />
            </g>
            </svg>
        </div>
      </section>

      <div className="faqs-page__content">
        <div className="faq-container">
          <div className="faq-column">
            {leftColumnData.map((faq, index) => (
              <div key={index} className={`faq-item ${openTab === index * 2 ? 'open' : ''}`}>
                <div className="faq-question" onClick={() => toggleTab(index * 2)}>
                  <FontAwesomeIcon icon={openTab === index * 2 ? faChevronUp : faQuestionCircle} />
                  <h3>{faq.question}</h3>
                </div>
                {openTab === index * 2 && (
                  <div className="faq-answer">
                    <p dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="faq-column">
            {rightColumnData.map((faq, index) => (
              <div key={index} className={`faq-item ${openTab === index * 2 + 1 ? 'open' : ''}`}>
                <div className="faq-question" onClick={() => toggleTab(index * 2 + 1)}>
                  <FontAwesomeIcon icon={openTab === index * 2 + 1 ? faChevronUp : faQuestionCircle} />
                  <h3>{faq.question}</h3>
                </div>
                {openTab === index * 2 + 1 && (
                  <div className="faq-answer">
                    <p dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="about-section about-cta">
        <div className="about-container">
          <div className="cta-content">
            <h2 className="cta-title">Ready for Your Unforgettable Miami Experience?</h2>
            <p className="cta-subtitle">The sea is calling. Let's create your perfect day on the water.</p>
            <button className="cta-button" onClick={() => setIsModalOpen(true)}>Book Your Adventure Now</button>
            <div className="cta-features">
              <div className="cta-feature-item">
                <FontAwesomeIcon icon={faShip} />
                <span>60+ Boats Available</span>
              </div>
              <div className="cta-feature-item">
                <img src={tripAdvisorIcon} alt="TripAdvisor Reviews" className="cta-tripadvisor-icon" />
                <span>Over 100 5-Star Reviews</span>
              </div>
              <div className="cta-feature-item">
                <FontAwesomeIcon icon={faStar} />
                <span>5000+ Happy Guests</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showPreloader && <WebPreloader />}
      {isModalOpen && <CustomBoatLeadModal onClose={() => setIsModalOpen(false)} />}
      {/* TODO: Modal de contacto si existe, como en AboutUsPage */}
    </div>
  );
};

export default FAQsPage;